import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FactCheckRounded } from '@mui/icons-material';
import TeacherLayout from './TeacherLayout';
import { useTeacherAuth } from '../../context/TeacherAuthContext';
import { resolveTeacherClassContext } from '../../utils/teacherClassContext';
import { getClassHomeworkStats } from '../../services/api';
import { ClassHomeworkStats } from '../../types';

const fmtDay = (iso: string): string => {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

// Color the bar by how much of the class finished the week's homework.
const barColor = (pct: number): string => {
  if (pct >= 70) return 'bg-emerald-500';
  if (pct >= 40) return 'bg-amber-500';
  return 'bg-rose-400';
};

const ClassHomework: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useTeacherAuth();
  const classContext = resolveTeacherClassContext(location.state as any);

  const school = classContext.school;
  const grade =
    typeof classContext.grade === 'number' ? classContext.grade : Number(classContext.grade);
  const section = classContext.section ?? null;
  const hasClassContext = Boolean(school && grade && !Number.isNaN(grade));

  const [stats, setStats] = useState<ClassHomeworkStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!hasClassContext || !token) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getClassHomeworkStats(token, grade, section);
        if (!cancelled) setStats(data);
      } catch (e: any) {
        if (!cancelled) setError(e.message || 'Failed to load homework stats');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hasClassContext, token, grade, section]);

  // Overall completion across all weeks (sum of completed / sum of possible).
  const overall = useMemo(() => {
    if (!stats || stats.weeks.length === 0) return null;
    const completed = stats.weeks.reduce((n, w) => n + w.completed, 0);
    const possible = stats.weeks.reduce((n, w) => n + w.possible, 0);
    return { completed, possible, pct: possible ? Math.round((100 * completed) / possible) : 0 };
  }, [stats]);

  if (!hasClassContext) {
    return (
      <TeacherLayout active="homework">
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-600">
          Please return to the Classes screen to pick a class before viewing homework.
          <div className="mt-4">
            <button
              type="button"
              onClick={() => navigate('/teacher-view')}
              className="rounded-xl bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700"
            >
              Pick a class
            </button>
          </div>
        </div>
      </TeacherLayout>
    );
  }

  return (
    <TeacherLayout active="homework">
      <div className="mx-auto w-full max-w-3xl">
        <div className="mb-1 flex items-center gap-2">
          <FactCheckRounded className="text-violet-600" />
          <h1 className="text-xl font-bold text-slate-900">Homework completion</h1>
        </div>
        <p className="mb-5 text-sm text-slate-500">
          Grade {grade}
          {section ? ` · Section ${section}` : ''} — how much of the homework you set is actually
          getting done, week by week.
        </p>

        {loading ? (
          <p className="py-10 text-center text-slate-500">Loading…</p>
        ) : error ? (
          <p className="py-10 text-center text-rose-600">{error}</p>
        ) : !stats || stats.weeks.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
            No homework recorded yet. Set homework on the Curriculum page and it will start showing
            here.
          </div>
        ) : (
          <>
            {overall && (
              <div className="mb-5 flex items-center justify-between rounded-2xl bg-violet-600 p-5 text-white">
                <div>
                  <p className="text-sm opacity-90">Overall completion</p>
                  <p className="text-3xl font-bold">{overall.pct}%</p>
                </div>
                <div className="text-right text-xs opacity-90">
                  <p>{stats.class_size} students</p>
                  <p>
                    {overall.completed} of {overall.possible} expected done
                  </p>
                </div>
              </div>
            )}

            <div className="rounded-2xl border border-[#E9E6F4] bg-white p-5 shadow-sm">
              <div className="flex flex-col gap-4">
                {stats.weeks.map((w) => (
                  <div key={w.week_start} className="flex items-center gap-3">
                    <div className="w-24 flex-none">
                      <p className="text-sm font-semibold text-slate-800">Week of {fmtDay(w.week_start)}</p>
                      <p className="text-xs text-slate-400">
                        {w.assigned} homework{w.assigned !== 1 ? 's' : ''}
                      </p>
                    </div>
                    <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-slate-100">
                      <div
                        className={`h-full rounded-full ${barColor(w.pct)}`}
                        style={{ width: `${w.pct}%` }}
                      />
                    </div>
                    <div className="w-28 flex-none text-right">
                      <span className="text-sm font-bold text-slate-800">{w.pct}%</span>
                      <span className="ml-1 text-xs text-slate-400">
                        {w.completed}/{w.possible}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <p className="mt-3 text-xs text-slate-400">
              The pair next to each bar is students-done / total-expected that week (every student ×
              every homework, {stats.class_size} students). Green ≥ 70%, amber ≥ 40%.
            </p>
          </>
        )}
      </div>
    </TeacherLayout>
  );
};

export default ClassHomework;
